import * as THREE from 'three';
import { strToU8, zipSync } from 'fflate';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';

export type MeshFormat = 'stl' | 'obj' | '3mf';
export type ExportUnits = 'mm' | 'in';
export type StlEncoding = 'binary' | 'ascii';

export type MeshExportOptions = {
  format: MeshFormat;
  orientation: boolean;
  scale: boolean;
  textures: boolean;
  units: ExportUnits;
  stlEncoding: StlEncoding;
  normals: boolean;
};

type Part = {
  name: string;
  positions: Float32Array;
  normals: Float32Array | null;
  uvs: Float32Array | null;
  indices: Uint32Array;
  texture: THREE.Texture | null;
  color: THREE.Color;
};

const fmt = (n: number) => (Number.isFinite(n) ? n.toFixed(5) : '0');

function sourceMaterial(mesh: THREE.Mesh): THREE.MeshStandardMaterial {
  const m = (mesh.userData.original ?? mesh.material) as THREE.Material | THREE.Material[];
  return (Array.isArray(m) ? m[0] : m) as THREE.MeshStandardMaterial;
}

export function modelHasTextures(model: THREE.Object3D): boolean {
  let found = false;
  model.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh && sourceMaterial(mesh).map && mesh.geometry.getAttribute('uv')) found = true;
  });
  return found;
}

export function fileExtension(opts: Pick<MeshExportOptions, 'format' | 'textures'>): string {
  if (opts.format === 'obj' && opts.textures) return 'zip';
  return opts.format;
}

/** Bakes every mesh into Z-up print space (the convention slicers expect), resting on Z = 0. */
function collectParts(model: THREE.Object3D, orientation: THREE.Quaternion, opts: MeshExportOptions): Part[] {
  model.updateMatrixWorld(true);
  const toPivot = model.parent ? model.parent.matrixWorld.clone().invert() : new THREE.Matrix4();
  let factor = 1;
  if (!opts.scale) factor /= (model.userData.importScale as number | undefined) ?? 1;
  if (opts.units === 'in') factor /= 25.4;
  const base = new THREE.Matrix4().makeScale(factor, factor, factor).multiply(new THREE.Matrix4().makeRotationX(Math.PI / 2));
  if (opts.orientation) base.multiply(new THREE.Matrix4().makeRotationFromQuaternion(orientation));

  const parts: Part[] = [];
  const v = new THREE.Vector3();
  model.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const g = mesh.geometry;
    const pos = g.getAttribute('position');
    if (!pos) return;
    const m = base.clone().multiply(toPivot).multiply(mesh.matrixWorld);
    const nm = new THREE.Matrix3().getNormalMatrix(m);
    const positions = new Float32Array(pos.count * 3);
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i).applyMatrix4(m);
      positions.set([v.x, v.y, v.z], i * 3);
    }
    const nor = g.getAttribute('normal');
    let normals: Float32Array | null = null;
    if (nor) {
      normals = new Float32Array(nor.count * 3);
      for (let i = 0; i < nor.count; i++) {
        v.fromBufferAttribute(nor, i).applyMatrix3(nm).normalize();
        normals.set([v.x, v.y, v.z], i * 3);
      }
    }
    const uv = g.getAttribute('uv');
    let uvs: Float32Array | null = null;
    if (uv) {
      uvs = new Float32Array(uv.count * 2);
      for (let i = 0; i < uv.count; i++) uvs.set([uv.getX(i), uv.getY(i)], i * 2);
    }
    const indices = g.index ? Uint32Array.from(g.index.array as ArrayLike<number>) : Uint32Array.from({ length: pos.count }, (_, i) => i);
    const mat = sourceMaterial(mesh);
    parts.push({
      name: (mesh.name || `part_${parts.length + 1}`).replace(/\s+/g, '_'),
      positions,
      normals,
      uvs,
      indices,
      texture: mat.map ?? null,
      color: mat.color ? mat.color.clone() : new THREE.Color('#ffffff')
    });
  });

  // Centre on X/Y and rest the lowest point on the platform.
  const min = new THREE.Vector3(Infinity, Infinity, Infinity);
  const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
  parts.forEach((p) => {
    for (let i = 0; i < p.positions.length; i += 3) {
      v.fromArray(p.positions, i);
      min.min(v);
      max.max(v);
    }
  });
  const shift = new THREE.Vector3(-(min.x + max.x) / 2, -(min.y + max.y) / 2, -min.z);
  parts.forEach((p) => {
    for (let i = 0; i < p.positions.length; i += 3) {
      p.positions[i] += shift.x;
      p.positions[i + 1] += shift.y;
      p.positions[i + 2] += shift.z;
    }
  });
  return parts;
}

function faceNormal(p: Float32Array, a: number, b: number, c: number) {
  const va = new THREE.Vector3().fromArray(p, a * 3);
  const vb = new THREE.Vector3().fromArray(p, b * 3);
  const vc = new THREE.Vector3().fromArray(p, c * 3);
  return vb.sub(va).cross(vc.sub(va)).normalize();
}

function writeStl(parts: Part[], encoding: StlEncoding, name: string): Blob {
  if (encoding === 'ascii') {
    const out: string[] = [`solid ${name}`];
    parts.forEach((p) => {
      for (let t = 0; t < p.indices.length; t += 3) {
        const [a, b, c] = [p.indices[t], p.indices[t + 1], p.indices[t + 2]];
        const n = faceNormal(p.positions, a, b, c);
        out.push(` facet normal ${fmt(n.x)} ${fmt(n.y)} ${fmt(n.z)}`, '  outer loop');
        [a, b, c].forEach((i) => out.push(`   vertex ${fmt(p.positions[i * 3])} ${fmt(p.positions[i * 3 + 1])} ${fmt(p.positions[i * 3 + 2])}`));
        out.push('  endloop', ' endfacet');
      }
    });
    out.push(`endsolid ${name}`);
    return new Blob([out.join('\n')], { type: 'model/stl' });
  }
  const triangles = parts.reduce((s, p) => s + p.indices.length / 3, 0);
  const buffer = new ArrayBuffer(84 + triangles * 50);
  const view = new DataView(buffer);
  const header = strToU8('Exported from PreForm');
  new Uint8Array(buffer, 0, 80).set(header.subarray(0, 80));
  view.setUint32(80, triangles, true);
  let o = 84;
  parts.forEach((p) => {
    for (let t = 0; t < p.indices.length; t += 3) {
      const [a, b, c] = [p.indices[t], p.indices[t + 1], p.indices[t + 2]];
      const n = faceNormal(p.positions, a, b, c);
      [n.x, n.y, n.z].forEach((x) => {
        view.setFloat32(o, x || 0, true);
        o += 4;
      });
      [a, b, c].forEach((i) => {
        for (let k = 0; k < 3; k++) {
          view.setFloat32(o, p.positions[i * 3 + k], true);
          o += 4;
        }
      });
      view.setUint16(o, 0, true);
      o += 2;
    }
  });
  return new Blob([buffer], { type: 'model/stl' });
}

async function textureToPng(tex: THREE.Texture): Promise<Uint8Array | null> {
  const img = tex.image as (CanvasImageSource & {width: number;height: number;}) | undefined;
  if (!img || !img.width || !img.height) return null;
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0);
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/png'));
  return blob ? new Uint8Array(await blob.arrayBuffer()) : null;
}

/** Exports each unique texture once, keyed by texture uuid. */
async function collectTextures(parts: Part[]) {
  const files = new Map<string, {file: string;data: Uint8Array;}>();
  for (const p of parts) {
    if (!p.texture || !p.uvs || files.has(p.texture.uuid)) continue;
    const data = await textureToPng(p.texture);
    if (data) files.set(p.texture.uuid, { file: `texture_${files.size + 1}.png`, data });
  }
  return files;
}

async function writeObj(parts: Part[], opts: MeshExportOptions, name: string): Promise<Blob> {
  const textures = opts.textures ? await collectTextures(parts) : new Map();
  const obj: string[] = ['# Exported from PreForm', `# Units: ${opts.units === 'mm' ? 'millimeters' : 'inches'}`];
  const mtl: string[] = ['# Exported from PreForm'];
  if (opts.textures) obj.push(`mtllib ${name}.mtl`);
  let vOff = 1;
  parts.forEach((p, pi) => {
    const tex = p.texture ? textures.get(p.texture.uuid) : undefined;
    const useUv = opts.textures && !!p.uvs;
    const useN = opts.normals && !!p.normals;
    obj.push(`o ${p.name}`);
    for (let i = 0; i < p.positions.length; i += 3) obj.push(`v ${fmt(p.positions[i])} ${fmt(p.positions[i + 1])} ${fmt(p.positions[i + 2])}`);
    if (useUv && p.uvs) for (let i = 0; i < p.uvs.length; i += 2) obj.push(`vt ${fmt(p.uvs[i])} ${fmt(1 - p.uvs[i + 1])}`);
    if (useN && p.normals) for (let i = 0; i < p.normals.length; i += 3) obj.push(`vn ${fmt(p.normals[i])} ${fmt(p.normals[i + 1])} ${fmt(p.normals[i + 2])}`);
    if (opts.textures) {
      const mat = `material_${pi + 1}`;
      obj.push(`usemtl ${mat}`);
      mtl.push(`newmtl ${mat}`, `Kd ${fmt(p.color.r)} ${fmt(p.color.g)} ${fmt(p.color.b)}`, 'Ka 0 0 0', 'Ks 0 0 0', 'd 1');
      if (tex) mtl.push(`map_Kd textures/${tex.file}`);
      mtl.push('');
    }
    const ref = (i: number) => {
      const k = i + vOff;
      if (useUv && useN) return `${k}/${k}/${k}`;
      if (useUv) return `${k}/${k}`;
      if (useN) return `${k}//${k}`;
      return `${k}`;
    };
    for (let t = 0; t < p.indices.length; t += 3) obj.push(`f ${ref(p.indices[t])} ${ref(p.indices[t + 1])} ${ref(p.indices[t + 2])}`);
    vOff += p.positions.length / 3;
  });
  const objText = obj.join('\n');
  if (!opts.textures) return new Blob([objText], { type: 'model/obj' });
  const files: Record<string, Uint8Array> = { [`${name}.obj`]: strToU8(objText), [`${name}.mtl`]: strToU8(mtl.join('\n')) };
  textures.forEach((t) => files[`textures/${t.file}`] = t.data);
  return new Blob([zipSync(files)], { type: 'application/zip' });
}

async function write3mf(parts: Part[], opts: MeshExportOptions): Promise<Blob> {
  const textures = opts.textures ? await collectTextures(parts) : new Map();
  const res: string[] = [];
  const items: string[] = [];
  let id = 1;
  const texIds = new Map<string, number>();
  textures.forEach((t, uuid) => {
    res.push(`<m:texture2d id="${id}" path="/3D/Textures/${t.file}" contenttype="image/png" />`);
    texIds.set(uuid, id++);
  });
  parts.forEach((p) => {
    let groupId = 0;
    const texId = p.texture ? texIds.get(p.texture.uuid) : undefined;
    if (texId && p.uvs) {
      groupId = id++;
      const coords: string[] = [];
      for (let i = 0; i < p.uvs.length; i += 2) coords.push(`<m:tex2coord u="${fmt(p.uvs[i])}" v="${fmt(1 - p.uvs[i + 1])}" />`);
      res.push(`<m:texture2dgroup id="${groupId}" texid="${texId}">${coords.join('')}</m:texture2dgroup>`);
    }
    const objId = id++;
    const verts: string[] = [];
    for (let i = 0; i < p.positions.length; i += 3) verts.push(`<vertex x="${fmt(p.positions[i])}" y="${fmt(p.positions[i + 1])}" z="${fmt(p.positions[i + 2])}" />`);
    const tris: string[] = [];
    for (let t = 0; t < p.indices.length; t += 3) {
      const [a, b, c] = [p.indices[t], p.indices[t + 1], p.indices[t + 2]];
      tris.push(groupId ? `<triangle v1="${a}" v2="${b}" v3="${c}" pid="${groupId}" p1="${a}" p2="${b}" p3="${c}" />` : `<triangle v1="${a}" v2="${b}" v3="${c}" />`);
    }
    res.push(`<object id="${objId}" name="${p.name}" type="model"><mesh><vertices>${verts.join('')}</vertices><triangles>${tris.join('')}</triangles></mesh></object>`);
    items.push(`<item objectid="${objId}" />`);
  });
  const model = `<?xml version="1.0" encoding="UTF-8"?>
<model unit="${opts.units === 'mm' ? 'millimeter' : 'inch'}" xml:lang="en-US" xmlns="http://schemas.microsoft.com/3dmanufacturing/core/2015/02" xmlns:m="http://schemas.microsoft.com/3dmanufacturing/material/2015/02">
<metadata name="Application">PreForm</metadata>
<resources>${res.join('\n')}</resources>
<build>${items.join('')}</build>
</model>`;
  const files: Record<string, Uint8Array> = {
    '[Content_Types].xml': strToU8(
      '<?xml version="1.0" encoding="UTF-8"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="model" ContentType="application/vnd.ms-package.3dmanufacturing-3dmodel+xml"/><Default Extension="png" ContentType="application/vnd.ms-package.3dmanufacturing-3dmodeltexture"/></Types>'
    ),
    '_rels/.rels': strToU8(
      '<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Target="/3D/3dmodel.model" Id="rel0" Type="http://schemas.microsoft.com/3dmanufacturing/2013/01/3dmodel"/></Relationships>'
    ),
    '3D/3dmodel.model': strToU8(model)
  };
  if (textures.size) {
    const rels: string[] = [];
    let r = 1;
    textures.forEach((t) => {
      files[`3D/Textures/${t.file}`] = t.data;
      rels.push(`<Relationship Target="/3D/Textures/${t.file}" Id="tex${r++}" Type="http://schemas.microsoft.com/3dmanufacturing/2013/01/3dtexture"/>`);
    });
    files['3D/_rels/3dmodel.model.rels'] = strToU8(
      `<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${rels.join('')}</Relationships>`
    );
  }
  return new Blob([zipSync(files)], { type: 'model/3mf' });
}

export async function exportMesh(model: THREE.Object3D, orientation: THREE.Quaternion, opts: MeshExportOptions, baseName: string): Promise<Blob> {
  const parts = collectParts(model, orientation, opts);
  if (parts.length === 0) throw new Error('The model has no geometry to export.');
  if (opts.format === 'stl') return writeStl(parts, opts.stlEncoding, baseName);
  if (opts.format === 'obj') return writeObj(parts, opts, baseName);
  return write3mf(parts, opts);
}

/** Packages the model (with its current orientation) and job settings into a .form archive. */
export async function exportBuildFile(model: THREE.Object3D, manifest: Record<string, unknown>): Promise<Blob> {
  const target = model.parent ?? model;
  const glb = (await new GLTFExporter().parseAsync(target, { binary: true })) as ArrayBuffer;
  return new Blob(
    [zipSync({ 'manifest.json': strToU8(JSON.stringify(manifest, null, 2)), 'models/model.glb': new Uint8Array(glb) })],
    { type: 'application/zip' }
  );
}

type SaveFilePicker = (opts: {suggestedName: string;}) => Promise<FileSystemFileHandle>;

/** Asks the browser for a save location. Returns null when cancelled; throws when unsupported. */
export async function pickSaveLocation(suggestedName: string): Promise<FileSystemFileHandle | null> {
  const picker = (window as unknown as {showSaveFilePicker?: SaveFilePicker;}).showSaveFilePicker;
  if (!picker) throw new Error('unsupported');
  try {
    return await picker({ suggestedName });
  } catch (err) {
    if ((err as DOMException).name === 'AbortError') return null;
    throw err;
  }
}

export async function saveBlob(blob: Blob, fileName: string, handle?: FileSystemFileHandle | null) {
  if (handle) {
    const writable = await (handle as FileSystemFileHandle & {createWritable: () => Promise<FileSystemWritableFileStream>;}).createWritable();
    await writable.write(blob);
    await writable.close();
    return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}
